import { QueryClient, QueryObserver } from "@tanstack/react-query";
import { startTransition, useEffect, useRef } from "react";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { Build, Job } from "~/components/build/Show/lib/types";
import { allJobsQueryKey } from "~/components/build/Show/lib/queries/allJobs";

export default function useSyncJobsToStore(
  queryClient: QueryClient,
  build: Build | undefined,
  showRetriedJobs: boolean,
) {
  const setJobs = useBuildStore((state) => state.setJobs);
  const lastDataUpdatedAt = useRef(0);

  useEffect(() => {
    if (!build) {
      return;
    }

    const observer = new QueryObserver<{ records: Job[] }>(queryClient, {
      queryKey: allJobsQueryKey(build.id, showRetriedJobs),
      // Don't execute the query, just listen for changes
      enabled: false,
    });

    const unsubscribe = observer.subscribe((result) => {
      // Check if the data has been updated since the last time we received it before
      // updating the store and triggering re-renders.
      if (result.dataUpdatedAt > lastDataUpdatedAt.current && result.data?.records) {
        lastDataUpdatedAt.current = result.dataUpdatedAt;
        startTransition(() => {
          setJobs(result.data.records);
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, build, showRetriedJobs, setJobs]);
}
